import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useChannelPlaybackNextMutation } from "./channelsApi";
import {
  hasPlayableTracks,
  type ChannelPreviewSource,
  type PreviewPick,
  type PreviewRotation,
} from "./channelPreviewPlayback";
import type { ChannelPlaybackAction } from "./types";

export interface UseChannelPreviewPlaybackArgs {
  channelId: string | undefined;
  stagingRotations: PreviewRotation[];
  productionRotations: PreviewRotation[];
  /** Kept for call-site compatibility; stream URL now comes from the API. */
  resolveStreamUrl?: (trackId: string) => string | null | undefined;
  /** When false, pause audio (e.g. flipped to front). */
  active: boolean;
}

export function useChannelPreviewPlayback({
  channelId,
  stagingRotations,
  productionRotations,
  active,
}: UseChannelPreviewPlaybackArgs) {
  const [source, setSource] = useState<ChannelPreviewSource>("production");
  const [isPlaying, setIsPlaying] = useState(false);
  const [current, setCurrent] = useState<PreviewPick | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const advancingRef = useRef(false);
  const sourceRef = useRef(source);
  const channelIdRef = useRef(channelId);
  sourceRef.current = source;
  channelIdRef.current = channelId;

  const [playbackNext] = useChannelPlaybackNextMutation();

  const rotations =
    source === "staging" ? stagingRotations : productionRotations;
  const playable = hasPlayableTracks(rotations);

  const ensureAudio = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = "auto";
    }
    return audioRef.current;
  }, []);

  const stopAudio = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    }
    setIsPlaying(false);
  }, []);

  const softResetPlayback = useCallback(() => {
    setCurrent(null);
    stopAudio();
  }, [stopAudio]);

  const requestAndPlay = useCallback(
    async (action: ChannelPlaybackAction) => {
      const id = channelIdRef.current;
      if (!id) {
        toast.error("No channel selected");
        return false;
      }
      if (advancingRef.current) return false;
      advancingRef.current = true;
      try {
        const data = await playbackNext({
          channelId: id,
          scheduleRole: sourceRef.current,
          action,
        }).unwrap();

        const pick: PreviewPick = {
          rotationId: data.rotationId,
          slotLabel: "",
          categoryLabel: "",
          trackId: data.trackId,
          label: data.artist
            ? `${data.title} — ${data.artist}`
            : data.title,
        };

        const audio = ensureAudio();
        audio.src = data.streamUrl;
        setCurrent(pick);
        try {
          await audio.play();
          setIsPlaying(true);
          return true;
        } catch {
          toast.error("Could not start channel preview playback");
          setIsPlaying(false);
          return false;
        }
      } catch (err) {
        const message =
          err && typeof err === "object" && "data" in err
            ? String(
                (err as { data?: { message?: string } }).data?.message ||
                  "Failed to get next track",
              )
            : err instanceof Error
              ? err.message
              : "Failed to get next track";
        toast.error(message);
        stopAudio();
        setCurrent(null);
        return false;
      } finally {
        advancingRef.current = false;
      }
    },
    [ensureAudio, playbackNext, stopAudio],
  );

  const play = useCallback(async () => {
    const audio = audioRef.current;
    if (current && audio?.src && !audio.ended) {
      try {
        await audio.play();
        setIsPlaying(true);
        return;
      } catch {
        // fall through to durable start
      }
    }
    await requestAndPlay("start");
  }, [current, requestAndPlay]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setIsPlaying(false);
  }, []);

  const skip = useCallback(async () => {
    await requestAndPlay("skip");
  }, [requestAndPlay]);

  const changeSource = useCallback(
    (next: ChannelPreviewSource) => {
      if (next === source) return;
      setSource(next);
      softResetPlayback();
    },
    [softResetPlayback, source],
  );

  useEffect(() => {
    softResetPlayback();
  }, [channelId, softResetPlayback]);

  useEffect(() => {
    const audio = ensureAudio();
    const onEnded = () => {
      void requestAndPlay("ended");
    };
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("ended", onEnded);
    };
  }, [ensureAudio, requestAndPlay]);

  useEffect(() => {
    if (!active) {
      stopAudio();
    }
  }, [active, stopAudio]);

  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      }
      audioRef.current = null;
    };
  }, []);

  return {
    source,
    changeSource,
    isPlaying,
    current,
    playable,
    play,
    pause,
    skip,
    stop: stopAudio,
    resetQueue: softResetPlayback,
  };
}
